import { useCallback, useEffect, useRef, useState } from "react";
import { Wallet } from "../../ui";
import { fetchMenuAiWalletAPI } from "../../../utils/api/menuAiWallet";
import { AI_WALLET_CHANGED, aiWalletMenuText, aiWalletRechargePath, reservedText, walletAmount } from "./aiWalletMenu";
import "./AiWallet.css";

// Compact AI Wallet balance next to the Menu page's AI actions:
//   "AI Wallet · 42.50 SAR"  -> Billing, AI Wallet section of the restaurant
//
// The balance is read from the backend (the wallet ledger) whenever the
// restaurant changes, an AI operation reports a change (notifyAiWalletChanged)
// or the tab becomes visible again (e.g. back from recharging). It is
// informational: the backend decides whether an operation may run.
// Renders nothing for users who may not see the wallet (or when it cannot be read).
export default function AiWalletIndicator({ restaurant, lang = "ar", reloadKey = 0, isMenu=false }) {
  const t = aiWalletMenuText(lang);
  const [wallet, setWallet] = useState(null);
  const latest = useRef(0);

  const load = useCallback(async () => {
    if (!restaurant) return setWallet(null);
    const call = (latest.current += 1);
    try {
      const next = await fetchMenuAiWalletAPI(restaurant);
      if (call === latest.current) setWallet(next);
    } catch (error) {
      if (call === latest.current) setWallet(null);
    }
    return undefined;
  }, [restaurant]);

  // Another restaurant: never show the previous one's balance meanwhile.
  useEffect(() => setWallet(null), [restaurant]);
  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(() => {
    const refresh = () => load();
    const onVisible = () => document.visibilityState === "visible" && load();
    window.addEventListener(AI_WALLET_CHANGED, refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(AI_WALLET_CHANGED, refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  if (!wallet?.visible) return null;
  const frozen = wallet.status === "frozen";
  // Warnings only matter while usage is actually deducted.
  const state = frozen ? "is-frozen" : wallet.charging && wallet.availableBalanceMinor <= 0 ? "is-empty" : wallet.charging && wallet.lowBalance ? "is-low" : "";
  const notes = [frozen ? t.frozen : state ? t.low : "", wallet.reservedMinor > 0 ? reservedText(wallet.reservedMinor, lang) : "", wallet.charging ? "" : t.notCharging].filter(Boolean);
  const amount = walletAmount(wallet.availableBalanceMinor, lang);

  return (
    <a
      className={`aiw-indicator ${state}`}
      href={aiWalletRechargePath(wallet.storeId)}
      title={[t.open, ...notes].join(" · ")}
      aria-label={`${t.label}: ${amount}${notes.length ? ` (${notes.join(", ")})` : ""}. ${t.open}`}
      data-testid="menu-ai-wallet"
    >
      <Wallet size={15} aria-hidden="true" />
      <span className="aiw-indicator-label">{isMenu?"":t.label}</span>
      <span aria-hidden="true">·</span>
      <strong className="aiw-indicator-amount" dir="ltr">
        {amount}
      </strong>
      {state && <span className="aiw-indicator-dot" aria-hidden="true" />}
    </a>
  );
}
